import React from "react";
import {
  Container,
  Grid,
  Segment,
  Loader,
  Button,
  Divider,
  Image,
  Icon,
} from "semantic-ui-react";
import TestataPagina from "../common/TestataPagina";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Preferenze from "./Preferenze";
import Calendar from "./Calendar";
import Timeline from "./Timeline";
import GrigliaMaestri from "./GrigliaMaestri";
import PupilDataModal from "./PupilDataModal";
import { Message } from "semantic-ui-react";

const PrivateNewMainDesktop = ({
  // Redux props
  datiScuola,
  datiScuolaOld,
  loading,
  // State from parent
  selectedArea,
  setSelectedArea,
  selectedSpecialita,
  setSelectedSpecialita,
  selectedLivello,
  setSelectedLivello,
  selectedLingua,
  setSelectedLingua,
  selectedNumAllevi,
  setSelectedNumAllevi,
  isPreferenzeValid,
  hasBeenInitialized,
  dateNonDisponibili,
  selectedDates,
  nomeMaestro,
  maestroData,
  idMaestro,
  setIdMaestro,
  disponibilità,
  primoGiorno,
  firstDayTimeSlots,
  timeRangeFirstDay,
  setTimeRangeFirstDay,
  timeRangesOtherDays,
  prezziLezioni,
  isMaestroMode,
  setIsMaestroMode,
  showMaestriModal,
  isConfirmEnabled,
  showPupilModal,
  isLoadingUnavailableDays,
  // Handlers from parent
  handleRecalculate,
  handleOpenMaestriModal,
  handleCloseMaestriModal,
  handleSelectMaestro,
  handleFirstDaySlotClick,
  handleMainCalendarDateChange,
  fetchBestAvailability,
  createTimeSlotsFromDisponibilita,
  createOtherDaySlotClickHandler,
  clearOtherDayTimeRange,
  handleConfirmLessons,
  handlePupilDataConfirm,
  handlePupilDataCancel,
}) => {
  const { t } = useTranslation();

  // Check if any day has no available timeslots
  const hasDaysWithNoAvailability = () => {
    if (!disponibilità || disponibilità.length === 0) return false;

    // Group disponibilità by date
    const groupedByDate = disponibilità.reduce((acc, disp) => {
      const { data, dalle, alle } = disp;
      if (!acc[data]) {
        acc[data] = [];
      }
      acc[data].push({ dalle, alle });
      return acc;
    }, {});

    // Check if any date has empty timeslots (all slots are unavailable)
    return Object.values(groupedByDate).some(
      (timeSlots) => timeSlots.length === 0
    );
  };

  // Function to render the floating lesson summary box
  const renderLessonSummary = () => {
    if (!prezziLezioni) return null;

    // Get the ordered list of lesson keys from prezziLezioni and sort them
    const orderedLessonKeys = Object.keys(prezziLezioni).sort();

    // Calculate totals: listPrice, actualPrice, deposit
    let totalListPrice = 0;
    let totalActualPrice = 0;
    let totalDeposit = 0;
    const currency = Array.isArray(prezziLezioni[orderedLessonKeys[0]])
      ? prezziLezioni[orderedLessonKeys[0]][0]?.currency || "EUR"
      : prezziLezioni[orderedLessonKeys[0]]?.currency || "EUR";

    orderedLessonKeys.forEach((lessonKey) => {
      const lessonPrice = prezziLezioni[lessonKey];
      if (Array.isArray(lessonPrice) && lessonPrice.length >= 3) {
        totalListPrice += lessonPrice[0]?.amount ?? 0;
        totalActualPrice += lessonPrice[1]?.amount ?? 0;
        totalDeposit += lessonPrice[2]?.amount ?? 0;
      }
    });

    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          right: "20px",
          transform: "translateY(-50%)",
          backgroundColor: "white",
          border: "2px solid #2185d0",
          borderRadius: "10px",
          padding: "20px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          zIndex: 1000,
          minWidth: "300px",
          maxWidth: "400px",
        }}
      >
        <h4
          style={{
            color: "#2185d0",
            marginTop: 0,
            marginBottom: "15px",
            textAlign: "center",
            borderBottom: "1px solid #e0e0e0",
            paddingBottom: "10px",
          }}
        >
          {t("riepilogo_lezioni")}
        </h4>

        <div style={{ marginBottom: "15px" }}>
          {orderedLessonKeys.map((lessonKey) => {
            const lessonPrice = prezziLezioni[lessonKey];

            // Parse the lesson key format: 2025-10-08_1000_1130
            const [datePart, startTime, endTime] = lessonKey.split("_");
            const formattedStartTime = `${startTime.slice(
              0,
              2
            )}:${startTime.slice(2)}`;
            const formattedEndTime = `${endTime.slice(0, 2)}:${endTime.slice(
              2
            )}`;

            // Extract prices if array format
            let listPrice = null;
            let actualPrice = null;
            let priceDisplay = null;

            if (Array.isArray(lessonPrice) && lessonPrice.length >= 2) {
              listPrice = lessonPrice[0]?.amount;
              actualPrice = lessonPrice[1]?.amount;
              const priceCurrency = lessonPrice[0]?.currency || "EUR";

              if (actualPrice === null || actualPrice === undefined) {
                priceDisplay = null;
              } else if (listPrice !== actualPrice) {
                // Show both prices with list price striked through
                priceDisplay = (
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#2185d0",
                      fontSize: "14px",
                    }}
                  >
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#999",
                        marginRight: "8px",
                      }}
                    >
                      {listPrice.toFixed(2)} {priceCurrency}
                    </span>
                    <span>
                      {actualPrice.toFixed(2)} {priceCurrency}
                    </span>
                  </div>
                );
              } else {
                // Show only one price
                priceDisplay = (
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#2185d0",
                      fontSize: "14px",
                    }}
                  >
                    {actualPrice.toFixed(2)} {priceCurrency}
                  </div>
                );
              }
            }

            return (
              <div
                key={lessonKey}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontWeight: "bold", fontSize: "14px" }}
                    className="black-label"
                  >
                    {moment(datePart, "YYYY-MM-DD").format("DD/MM/YYYY")}
                  </div>
                  {formattedStartTime === "00:00" && (
                    <div style={{ fontSize: "14px", color: "red" }}>
                      {t("selezionare_orario")}
                    </div>
                  )}
                  {formattedStartTime !== "00:00" && (
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {formattedStartTime} - {formattedEndTime}
                    </div>
                  )}
                </div>
                {priceDisplay}
              </div>
            );
          })}
        </div>

        <div
          style={{
            borderTop: "2px solid #2185d0",
            paddingTop: "10px",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: totalDeposit !== totalActualPrice ? "10px" : "0",
            }}
          >
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
              {t("totale")}
            </span>
            <span
              style={{ fontWeight: "bold", fontSize: "18px", color: "#2185d0" }}
            >
              {totalListPrice !== totalActualPrice && (
                <span
                  style={{
                    textDecoration: "line-through",
                    color: "#999",
                    marginRight: "8px",
                  }}
                >
                  {totalListPrice.toFixed(2)} {currency}
                </span>
              )}
              <span>
                {totalActualPrice.toFixed(2)} {currency}
              </span>
            </span>
          </div>

          {totalDeposit !== totalActualPrice && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "14px", color: "#666" }}>
                {t("anticipo_versare")}
              </span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#2185d0",
                }}
              >
                {totalDeposit.toFixed(2)} {currency}
              </span>
            </div>
          )}
        </div>

        {!isConfirmEnabled || hasDaysWithNoAvailability() ? (
          <div
            style={{
              border: "2px solid #f2711c",
              borderRadius: "8px",
              padding: "15px",
              backgroundColor: "#fff6f0",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#f2711c",
                fontWeight: "bold",
                fontSize: "1.1em",
                marginBottom: "10px",
              }}
            >
              {t("selezione_non_disponibile")}
            </div>
            <div style={{ fontSize: "0.9em", color: "#666" }}>
              {t("nessun_maestro_per_richiesta")}
            </div>
          </div>
        ) : (
          <Button
            primary
            fluid
            size="large"
            onClick={handleConfirmLessons}
            style={{
              backgroundColor: "#2185d0",
              marginTop: "10px",
            }}
          >
            {t("conferma")}
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container>
        <div className="head-offset"></div>
        <TestataPagina
          titolo={t("lezioni_private")}
          icon="user"
          sottoTitolo={t("scegli_preferenze_private")}
        />
        <Segment>
          <Loader active inline="centered" />
        </Segment>
      </Container>
    );
  }

  return (
    <Container>
      <div className="head-offset"></div>
      <TestataPagina
        titolo={t("lezioni_private")}
        icon="user"
        sottoTitolo={t("scegli_preferenze_private")}
      />
      <Segment>
        <Grid stackable columns={2}>
          <Grid.Column width={4}>
            <Preferenze
              datiScuola={datiScuola}
              selectedArea={selectedArea}
              setSelectedArea={setSelectedArea}
              selectedSpecialita={selectedSpecialita}
              setSelectedSpecialita={setSelectedSpecialita}
              selectedLivello={selectedLivello}
              setSelectedLivello={setSelectedLivello}
              selectedLingua={selectedLingua}
              setSelectedLingua={setSelectedLingua}
              selectedNumAllevi={selectedNumAllevi}
              setSelectedNumAllevi={setSelectedNumAllevi}
            />
            {/* Maestro Selection */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem" }}>
                <Icon name="graduation cap" style={{ marginRight: "0.5rem" }} />
                {t("maestro")}{" "}
                <span
                  style={{
                    fontStyle: "italic",
                    color: "#999",
                    fontSize: "0.9em",
                  }}
                >
                  ({t("opzionale")})
                </span>
              </label>
              {!isMaestroMode ? (
                <Button
                  fluid
                  onClick={handleOpenMaestriModal}
                  style={{
                    textAlign: "left",
                    justifyContent: "flex-start",
                    backgroundColor: "#fff",
                    color: "rgba(0,0,0,.87)",
                    border: "1px solid rgba(34,36,38,.15)",
                  }}
                >
                  <Icon name="search" style={{ marginRight: "0.5rem" }} />
                  {t("scegli_maestro")}
                </Button>
              ) : (
                <div
                  style={{
                    border: "1px solid rgba(34, 36, 38, 0.15)",
                    borderRadius: "0.28571429rem",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Image
                      src={
                        maestroData?.picture
                          ? `https://skiwork.blob.core.windows.net/webimg/${datiScuolaOld?.codiceScuola?.toUpperCase()}/Maestri/${
                              maestroData.picture
                            }`
                          : maestroData?.sesso === "F"
                          ? require("../../assets/images/people/AvatarMaestra.jpg")
                          : require("../../assets/images/people/AvatarMaestro.jpg")
                      }
                      avatar
                      size="mini"
                      onError={(e) => {
                        e.target.src =
                          maestroData?.sesso === "F"
                            ? require("../../assets/images/people/AvatarMaestra.jpg")
                            : require("../../assets/images/people/AvatarMaestro.jpg");
                      }}
                      style={{ marginRight: "0.75rem" }}
                    />
                    <span style={{ fontWeight: "bold", flex: 1 }}>
                      {nomeMaestro}
                    </span>
                  </div>
                  <Button
                    fluid
                    secondary
                    size="small"
                    onClick={() => {
                      setIsMaestroMode(false);
                      setIdMaestro(-1);
                    }}
                  >
                    <Icon name="close" />
                    {t("rimuovi_selezione")}
                  </Button>
                </div>
              )}
            </div>
          </Grid.Column>
          {/* Right column */}
          <Grid.Column textAlign="center" width={12}>
            {/* Info Web Section */}
            {(() => {
              const infoWebItem = datiScuolaOld?.infoWeb?.find(
                (item) => item.sezione === 1 && item.ordine === 0
              );
              if (
                infoWebItem &&
                infoWebItem.titolo &&
                infoWebItem.titolo.trim() !== ""
              ) {
                return (
                  <Segment
                    basic
                    style={{ textAlign: "left", paddingBottom: "20px" }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      {infoWebItem.icona && (
                        <Image
                          src={require(`../../assets/images/infoweb/${infoWebItem.icona}.png`)}
                          size="mini"
                          style={{ marginRight: "15px", marginTop: "5px" }}
                        />
                      )}
                      <div
                        dangerouslySetInnerHTML={{ __html: infoWebItem.titolo }}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </Segment>
                );
              }
              return null;
            })()}
            {isLoadingUnavailableDays ? (
              <Segment
                style={{
                  minHeight: "400px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <Loader active inline="centered" size="large" />
                  <p style={{ marginTop: "20px", color: "#666" }}>
                    {t("caricamento_disponibilita") ||
                      "Caricamento disponibilità..."}
                  </p>
                </div>
              </Segment>
            ) : (
              <>
                <Calendar
                  isPreferenzeValid={isPreferenzeValid}
                  dateNonDisponibili={dateNonDisponibili}
                  selectedDates={selectedDates}
                  onDateChange={handleMainCalendarDateChange}
                />
                <Divider />
                {!isPreferenzeValid && hasBeenInitialized && (
                  <Message info>
                    <Button onClick={handleRecalculate}>
                      {t("ricalcola_disponibilita")}
                    </Button>
                  </Message>
                )}
                {isPreferenzeValid &&
                  selectedDates?.length > 0 &&
                  disponibilità?.length === 0 && (
                    <>
                      <h3>{t("orario_primo_giorno")}</h3>
                      <Timeline
                        timeSlots={firstDayTimeSlots}
                        tempTimeRange={timeRangeFirstDay}
                        onTimeSlotClick={handleFirstDaySlotClick}
                        onClearTimeRange={() => setTimeRangeFirstDay(null)}
                        getSelectionInstructionText={() =>
                          t("seleziona_orario_lezioni")
                        }
                        showConfirmButton={true}
                        onTimeRangeConfirm={async () => {
                          await fetchBestAvailability();
                        }}
                        minHour={datiScuola.orarioDalle}
                        maxHour={datiScuola.orarioAlle}
                        minOreAcquistabili={datiScuola.minOreAcquistabili}
                        maxOreAcquistabiliAlGiorno={
                          datiScuola.maxOreAcquistabiliAlGiorno
                        }
                        permettiEccezioneMinOreInizio={
                          datiScuola.permettiEccezioneMinOreInizio
                        }
                        permettiEccezioneMinOreFine={
                          datiScuola.permettiEccezioneMinOreFine
                        }
                        fasceSelezioneOre={datiScuola.fasceSelezioneOre}
                        currentDate={primoGiorno}
                      />
                    </>
                  )}
                {disponibilità?.length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    {console.log(
                      "Rendering disponibilità section. timeRangesOtherDays:",
                      timeRangesOtherDays
                    )}
                    <h3>{t("conferma_orario_giornate")}</h3>
                    {/* <Message info>
                      <Message.Header>Orario lezioni</Message.Header>
                      <p>
                        Conferma l'orario delle lezioni per ogni giorno selezionato
                      </p>
                    </Message> */}
                    {(() => {
                      // Group disponibilità by date
                      const groupedByDate = disponibilità.reduce(
                        (acc, disp) => {
                          const { data, dalle, alle } = disp;
                          if (!acc[data]) {
                            acc[data] = [];
                          }
                          acc[data].push({ dalle, alle });
                          return acc;
                        },
                        {}
                      );

                      // Convert to array and map
                      return Object.entries(groupedByDate).map(
                        ([data, timeSlots]) => {
                          // Combine all timeslots for this day
                          const combinedTimeSlots = timeSlots.flatMap((slot) =>
                            createTimeSlotsFromDisponibilita(
                              slot.dalle,
                              slot.alle
                            )
                          );
                          const tempRange = timeRangesOtherDays[data] || null;

                          return (
                            <div key={data} style={{ marginBottom: "5px" }}>
                              <Timeline
                                data={moment(data).format("DD/MM/YYYY")}
                                timeSlots={combinedTimeSlots}
                                tempTimeRange={tempRange}
                                onTimeSlotClick={createOtherDaySlotClickHandler(
                                  data
                                )}
                                onClearTimeRange={() =>
                                  clearOtherDayTimeRange(data)
                                }
                                getSelectionInstructionText={() => ""}
                                showConfirmButton={false}
                                minHour={datiScuola.orarioDalle}
                                maxHour={datiScuola.orarioAlle}
                                minOreAcquistabili={
                                  datiScuola.minOreAcquistabili
                                }
                                maxOreAcquistabiliAlGiorno={
                                  datiScuola.maxOreAcquistabiliAlGiorno
                                }
                                permettiEccezioneMinOreInizio={
                                  datiScuola.permettiEccezioneMinOreInizio
                                }
                                permettiEccezioneMinOreFine={
                                  datiScuola.permettiEccezioneMinOreFine
                                }
                                fasceSelezioneOre={datiScuola.fasceSelezioneOre}
                                currentDate={moment(data)}
                              />
                            </div>
                          );
                        }
                      );
                    })()}
                  </div>
                )}
              </>
            )}
            {/* Info Web Section - Bottom Items */}
            {(() => {
              const infoWebItem1 = datiScuolaOld?.infoWeb?.find(
                (item) => item.sezione === 1 && item.ordine === 1
              );
              const infoWebItem2 = datiScuolaOld?.infoWeb?.find(
                (item) => item.sezione === 1 && item.ordine === 2
              );

              return (
                <>
                  {infoWebItem1 &&
                    infoWebItem1.titolo &&
                    infoWebItem1.titolo.trim() !== "" && (
                      <Segment
                        basic
                        style={{ textAlign: "left", paddingTop: "20px" }}
                      >
                        <div
                          style={{ display: "flex", alignItems: "flex-start" }}
                        >
                          {infoWebItem1.icona && (
                            <Image
                              src={require(`../../assets/images/infoweb/${infoWebItem1.icona}.png`)}
                              size="mini"
                              style={{ marginRight: "15px", marginTop: "5px" }}
                            />
                          )}
                          <div
                            dangerouslySetInnerHTML={{
                              __html: infoWebItem1.titolo,
                            }}
                            style={{ flex: 1 }}
                          />
                        </div>
                      </Segment>
                    )}
                  {infoWebItem2 &&
                    infoWebItem2.titolo &&
                    infoWebItem2.titolo.trim() !== "" && (
                      <Segment
                        basic
                        style={{ textAlign: "left", paddingTop: "20px" }}
                      >
                        <div
                          style={{ display: "flex", alignItems: "flex-start" }}
                        >
                          {infoWebItem2.icona && (
                            <Image
                              src={require(`../../assets/images/infoweb/${infoWebItem2.icona}.png`)}
                              size="mini"
                              style={{ marginRight: "15px", marginTop: "5px" }}
                            />
                          )}
                          <div
                            dangerouslySetInnerHTML={{
                              __html: infoWebItem2.titolo,
                            }}
                            style={{ flex: 1 }}
                          />
                        </div>
                      </Segment>
                    )}
                </>
              );
            })()}
          </Grid.Column>
        </Grid>
      </Segment>
      {/* Floating lesson summary box */}
      {renderLessonSummary()}

      {/* Maestri Selection Modal */}
      <GrigliaMaestri
        isOpen={showMaestriModal}
        onClose={handleCloseMaestriModal}
        onSelectMaestro={handleSelectMaestro}
        idScuola={datiScuolaOld?.id}
        codScuola={datiScuolaOld?.codiceScuola}
      />

      {/* Pupil Data Modal */}
      <PupilDataModal
        isOpen={showPupilModal}
        onClose={handlePupilDataCancel}
        onConfirm={handlePupilDataConfirm}
        numberOfPupils={selectedNumAllevi}
      />
    </Container>
  );
};

export default PrivateNewMainDesktop;
